import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { FiCheckCircle, FiCopy, FiRefreshCw, FiX } from 'react-icons/fi';
import { api } from '../../services/api';

const STATUS_MAP = {
  pending: 'payment.nowpayments.statusPending',
  confirming: 'payment.nowpayments.statusConfirming',
  underpaid: 'payment.nowpayments.statusUnderpaid',
  confirmed: 'payment.nowpayments.statusConfirmed',
  settling: 'payment.nowpayments.statusSettling',
  settled: 'payment.nowpayments.statusSettled',
  expired: 'payment.nowpayments.statusExpired',
  failed: 'payment.nowpayments.statusFailed',
};

export function NowPaymentsWidget({ config, onClose, onSuccess, variant = 'modal' }) {
  const { t } = useTranslation();
  const [copiedAddress, setCopiedAddress] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [orderStatus, setOrderStatus] = useState(config?.status || 'pending');
  const [timeLeft, setTimeLeft] = useState(null);
  const [polling, setPolling] = useState(false);

  const payAddress = config?.pay_address || config?.payAddress || '';
  const payUri = config?.pay_uri || config?.invoice_url || '';
  const chain = (config?.chain || '').toUpperCase();
  const token = (config?.token || '').toUpperCase();
  const expiresAt = useMemo(() => {
    if (!config?.expires_at) return null;
    const date = new Date(config.expires_at);
    return Number.isNaN(date.getTime()) ? null : date;
  }, [config?.expires_at]);
  const isSuccess = ['settled', 'completed'].includes(orderStatus);

  useEffect(() => {
    if (!expiresAt || isSuccess) {
      setTimeLeft(null);
      return undefined;
    }
    const tick = () => {
      const diff = expiresAt.getTime() - Date.now();
      setTimeLeft(diff > 0 ? diff : 0);
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [expiresAt, isSuccess]);

  const pollStatus = async (forceRefresh = false) => {
    if (!config?.payment_order_id) return;
    setPolling(true);
    try {
      const data = await api.getPaymentOrder(config.payment_order_id, { refresh: forceRefresh });
      if (data.code === 'SUCCESS') {
        const status = data.data?.display_status || data.data?.status || 'pending';
        setOrderStatus(status);
        if (['settled', 'completed'].includes(status)) {
          onSuccess?.();
        }
      }
    } catch (err) {
      console.error('Payment polling failed:', err);
    } finally {
      setPolling(false);
    }
  };

  useEffect(() => {
    if (!config?.payment_order_id || isSuccess) return undefined;
    pollStatus();
    const timer = window.setInterval(pollStatus, 10000);
    return () => window.clearInterval(timer);
  }, [config?.payment_order_id, isSuccess]);

  if (!config) return null;

  const handleCopy = async (value, setFlag) => {
    try {
      await navigator.clipboard.writeText(value);
      setFlag(true);
      window.setTimeout(() => setFlag(false), 1500);
    } catch (err) {
      setFlag(false);
    }
  };

  const formatTimeLeft = () => {
    if (timeLeft === null) return '--:--';
    const totalSeconds = Math.max(0, Math.floor(timeLeft / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const restMinutes = minutes % 60;
      return `${hours}h ${restMinutes}m`;
    }
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  };

  const statusLabel = t(STATUS_MAP[orderStatus] || STATUS_MAP.pending);
  const handleDone = () => {
    if (onClose) {
      onClose();
      return;
    }
    if (onSuccess) {
      onSuccess();
      return;
    }
    if (variant === 'page') {
      window.location.assign('/');
    }
  };

  const content = (
    <div className="relative w-full max-w-lg rounded-3xl bg-surface/95 border border-glass-border shadow-[0_24px_50px_rgba(6,12,18,0.45)] p-6">
      {variant !== 'page' && onClose && (
        <button
          onClick={onClose}
          className="absolute right-5 top-5 text-gray-400 hover:text-white"
        >
          <FiX size={20} />
        </button>
      )}

      <h3 className="text-xl font-semibold text-white">
        {t('payment.nowpayments.title')}
      </h3>
      {!isSuccess && (
        <p className="text-sm text-gray-400 mt-2">
          {t('payment.nowpayments.subtitle')}
        </p>
      )}

      {isSuccess ? (
        <div className="mt-6 space-y-5">
          <div className="flex flex-col items-center text-center gap-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-positive/15 text-positive">
              <FiCheckCircle size={28} />
            </div>
            <div>
              <div className="text-2xl font-semibold text-white">
                {t('payment.nowpayments.successTitle')}
              </div>
              <div className="text-sm text-gray-400 mt-1">
                {t('payment.nowpayments.successSubtitle')}
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-white/5 bg-white/3 p-4 text-sm text-gray-300">
            <div className="flex justify-between">
              <span>{t('payment.nowpayments.amount')}</span>
              <span className="text-white font-medium">
                {config.amount} {token || config.currency}
              </span>
            </div>
            <div className="flex justify-between mt-2">
              <span>{t('payment.nowpayments.networkLabel')}</span>
              <span className="text-white font-medium">{chain || '-'}</span>
            </div>
            <div className="flex justify-between mt-2">
              <span>{t('payment.nowpayments.tokenLabel')}</span>
              <span className="text-white font-medium">{token || '-'}</span>
            </div>
          </div>
          <button
            onClick={handleDone}
            className="w-full rounded-xl bg-positive text-slate-900 py-3 font-semibold hover:opacity-90 transition-opacity"
          >
            {t('payment.nowpayments.done')}
          </button>
        </div>
      ) : (
        <>
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-white/5 bg-white/3 p-4 text-sm text-gray-300">
              <div className="flex justify-between">
                <span>{t('payment.nowpayments.amount')}</span>
                <span className="text-white font-medium">
                  {config.amount} {token || config.currency}
                </span>
              </div>
              <div className="flex justify-between mt-2">
                <span>{t('payment.nowpayments.networkLabel')}</span>
                <span className="text-white font-medium">{chain || '-'}</span>
              </div>
              <div className="flex justify-between mt-2">
                <span>{t('payment.nowpayments.tokenLabel')}</span>
                <span className="text-white font-medium">{token || '-'}</span>
              </div>
              <div className="flex justify-between mt-2">
                <span>{t('payment.nowpayments.expiresIn')}</span>
                <span className="text-white font-medium">{formatTimeLeft()}</span>
              </div>
            </div>

            <div className="rounded-2xl border border-white/5 bg-white/3 p-4 text-sm text-gray-300 flex items-center justify-center">
              <div className="bg-white rounded-2xl p-3">
                <QRCodeSVG value={payUri || payAddress} size={140} includeMargin={true} />
              </div>
            </div>
          </div>

          <div className="mt-4 text-sm text-gray-300">
            <div className="flex items-center justify-between">
              <span>{t('payment.nowpayments.statusLabel')}</span>
              <span className="text-white font-medium">{statusLabel}</span>
            </div>
          </div>

          <div className="mt-4 space-y-3">
            <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-200">
              <span className="text-gray-400">{t('payment.nowpayments.payAddressLabel')}</span>
              <span className="flex-1 truncate text-white font-mono">{payAddress || '-'}</span>
              <button
                onClick={() => handleCopy(payAddress, setCopiedAddress)}
                className="shrink-0 text-xs text-accent hover:text-white"
              >
                {copiedAddress ? t('payment.nowpayments.copied') : t('payment.nowpayments.copyAddress')}
              </button>
            </div>
            <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-200">
              <span className="text-gray-400">{t('payment.nowpayments.payUriLabel')}</span>
              <span className="flex-1 truncate text-white font-mono">{payUri || '-'}</span>
              <button
                onClick={() => handleCopy(payUri, setCopiedLink)}
                className="shrink-0 text-xs text-accent hover:text-white"
              >
                {copiedLink ? t('payment.nowpayments.copied') : t('payment.nowpayments.copyLink')}
              </button>
            </div>
          </div>

          <div className="mt-5">
            <button
              onClick={pollStatus}
              className="w-full rounded-xl border border-white/10 px-4 py-3 text-sm text-slate-200 hover:text-white"
              disabled={polling}
            >
              <span className="inline-flex items-center gap-2">
                <FiRefreshCw className={polling ? 'animate-spin' : ''} />
                {t('payment.nowpayments.refreshStatus')}
              </span>
            </button>
          </div>
        </>
      )}
    </div>
  );

  if (variant === 'page') {
    return (
      <div className="min-h-screen bg-surface/95 text-white flex items-center justify-center px-6 py-10">
        {content}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      {content}
    </div>
  );
}
